// filepath: apps/web/src/features/tasks/useClaimTask.ts
//
// TanStack Query mutation hook for claiming a task (FR-HT-2 / FR-UI-TASK-1).
//
// AC-3: Claiming a task optimistically removes it from the unclaimed tab
// and adds it to the "My Tasks" tab, then confirms with the server.
//
// AC-2 side-effect: the unclaimed list refetches so the claimed task
// disappears from it (no stale unclaimed items after claim).
//
// ISS-377 round 3 (BA-authorized option (b)) R2-B2.5: the raw
// `fetch(url, init)` was migrated to the canonical `apiFetch`
// client. The previous shape attached NO `Authorization: Bearer
// <access_token>` header (the Go server's
// `internal/auth.AuthMiddleware` requires the bearer at
// `auth.go:51-62`); every call silently 401'd in production.
// Routing through `apiFetch` attaches the bearer via the
// registered OIDC accessor and runs the 401 → silent-refresh →
// retry chain. The static-guard forbidlist's regex extension
// (R2-B2.5) now matches the variable-arg `fetch(url, init)` shape
// that previously slipped past the guard.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import type { MutateOptions } from "@tanstack/react-query";
import { taskKeys } from "./useTasks";

/** POST /api/tasks/{id}/claim — no body required. */
interface ClaimResponse {
  id: string;
  status: string;
}

interface ClaimTaskVariables {
  taskId: string;
}

export function useClaimTask(apiBase = "/api"): {
  mutate: (
    variables: ClaimTaskVariables,
    opts?: MutateOptions<ClaimResponse, ApiError, ClaimTaskVariables>,
  ) => void;
  mutateAsync: (
    variables: ClaimTaskVariables,
  ) => Promise<ClaimResponse | undefined>;
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
} {
  const { tenantSlug } = useTenant();
  const queryClient = useQueryClient();

  const mutation = useMutation<ClaimResponse, ApiError, ClaimTaskVariables>({
    mutationFn: async ({ taskId }: ClaimTaskVariables) => {
      const url = `${apiBase}/tasks/${encodeURIComponent(taskId)}/claim`;
      // ISS-377 round 3 R2-B2.5: migrated to apiFetch.
      const response = await apiFetch(url, {
        method: "POST",
        init: {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({}),
        },
      });

      if (!response.ok) {
        const code = `http_${response.status}`;
        // 409 = already claimed by another user — surface as a conflict.
        // The caller handles 409 gracefully (task is gone from unclaimed anyway).
        throw new ApiError(
          response.statusText || "claim failed",
          response.status,
          code,
        );
      }

      return response.json() as Promise<ClaimResponse>;
    },

    onSuccess: () => {
      // Refetch both views so the claimed task moves from unclaimed → mine.
      void queryClient.invalidateQueries({
        queryKey: taskKeys.inbox(tenantSlug, "mine"),
      });
      void queryClient.invalidateQueries({
        queryKey: taskKeys.inbox(tenantSlug, "unclaimed"),
      });
    },

    // Do NOT auto-retry on 409 — the task may already be claimed.
    // Non-409 errors can be retried up to 2 times.
    retry: (count, err) => {
      if (
        err instanceof Error &&
        "status" in err &&
        (err as unknown as { status: number }).status === 409
      ) {
        return false;
      }
      return count < 2;
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
}
